/* ============================================================
   Dathent — Dashboard coachmark tour
   Port of design/pages/dashboard-tour.jsx to ES modules.

   Triggers from a `?tour=1` URL param. Walks the user through:
     1. Autopilot toggle  (.ap-toggle)
     2. Engagement chart  (.chart-card)
     3. Assistant FAB     (.as-fab)

   Keyboard: ←/→ to move, Esc to close.
   On close: clears `?tour=1` from URL and stores a localStorage flag
   so we don't re-trigger if the user revisits with the same query.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icons as I } from './pack-icons';

const TOUR_STEPS = [
  {
    id: 'autopilot',
    selector: '.ap-toggle',
    title: 'Autopilot runs your weekly cycle',
    body: "Plan → generate → publish → review — Dathent runs the loop on its own. Toggle off any time to drive manually.",
    placement: 'bottom',
  },
  {
    id: 'chart',
    selector: '.chart-card',
    title: 'Watch engagement, not your inbox',
    body: 'Hover the line to see daily numbers. We surface what changed and why, not just the chart.',
    placement: 'top',
  },
  {
    id: 'assistant',
    selector: '.as-fab',
    title: 'Press ⌘K — your operator answers',
    body: 'Ask the assistant to draft a post, connect a channel, or pull your numbers. It can act, not just answer.',
    placement: 'left',
  },
];

const TOUR_SEEN_KEY = 'dathent.dashboard-tour.seen';

export function DashboardTour({ onClose }) {
  // Filter out steps whose target isn't on the page right now (e.g. the
  // autopilot toggle is hidden in the first-run scenario). We do this once
  // at mount + on visibility changes so the tour gracefully degrades for
  // brand-new accounts instead of dying silently when step 1 has no target.
  const [steps, setSteps] = useState(() => TOUR_STEPS);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // On mount: drop steps with missing selectors. Retry once after a short
  // delay so late-loading widgets get a chance to render their targets.
  useEffect(() => {
    const filterSteps = () => {
      const present = TOUR_STEPS.filter((s) => document.querySelector(s.selector));
      if (present.length === 0) {
        // No targets at all — close the tour rather than show an empty mask
        onClose?.();
        return;
      }
      setSteps(present);
      setIdx((i) => Math.min(i, present.length - 1));
    };
    filterSteps();
    const retry = setTimeout(filterSteps, 600);
    return () => clearTimeout(retry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = steps[idx] || steps[0];

  // Track the highlighted element's position. Re-measures on resize, scroll,
  // and on a 200ms interval (covers layout shifts from late-loading widgets).
  useEffect(() => {
    if (!step) return;
    let raf;
    const update = () => {
      const el = document.querySelector(step.selector);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      if (r.top < 100 || r.top > window.innerHeight - 100) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    update();
    const onResize = () => { raf = requestAnimationFrame(update); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    const interval = setInterval(update, 200);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
      clearInterval(interval);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [idx, step?.selector]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowRight' && idx < steps.length - 1) setIdx(idx + 1);
      if (e.key === 'ArrowLeft' && idx > 0) setIdx(idx - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [idx, steps.length]);

  function close() {
    try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch {}
    onClose?.();
  }

  if (!rect || !step) return null;

  // Position the popover
  const pad = 8;
  const popW = 320;
  const popH = 180;
  let popTop, popLeft, arrowSide;
  if (step.placement === 'bottom') {
    popTop = rect.top + rect.height + pad + 4;
    popLeft = Math.max(16, Math.min(window.innerWidth - popW - 16, rect.left + rect.width / 2 - popW / 2));
    arrowSide = 'top';
  } else if (step.placement === 'top') {
    popTop = Math.max(16, rect.top - popH - pad - 4);
    popLeft = Math.max(16, Math.min(window.innerWidth - popW - 16, rect.left + rect.width / 2 - popW / 2));
    arrowSide = 'bottom';
  } else if (step.placement === 'left') {
    popTop = Math.max(16, rect.top + rect.height / 2 - 60);
    popLeft = Math.max(16, rect.left - popW - pad - 8);
    arrowSide = 'right';
  } else {
    popTop = rect.top;
    popLeft = rect.left + rect.width + pad + 4;
    arrowSide = 'left';
  }

  return (
    <>
      {/* Dimmed backdrop with a cut-out around the target */}
      <svg className="ct-mask" viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}>
        <defs>
          <mask id="ct-cutout">
            <rect width="100%" height="100%" fill="white" />
            <rect x={rect.left - 6} y={rect.top - 6}
                  width={rect.width + 12} height={rect.height + 12}
                  rx="12" fill="black" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(8,9,12,0.55)" mask="url(#ct-cutout)" />
      </svg>

      {/* Pulsing highlight ring */}
      <div className="ct-ring" style={{
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
      }} />

      {/* Popover */}
      <div className={`ct-pop ct-arrow-${arrowSide}`}
           style={{ top: popTop, left: popLeft, width: popW }}>
        <div className="ct-pop-h">
          <span className="ct-pop-step">{idx + 1} of {steps.length}</span>
          <button type="button" className="ct-pop-close" onClick={close} aria-label="Close tour">
            <I.x size={13} />
          </button>
        </div>
        <h4 className="ct-pop-title">{step.title}</h4>
        <p className="ct-pop-body">{step.body}</p>
        <div className="ct-pop-foot">
          <div className="ct-dots">
            {steps.map((_, i) => (
              <span key={i} className={i === idx ? 'is-active' : ''} />
            ))}
          </div>
          <div className="ct-pop-actions">
            {idx > 0 && (
              <button type="button" className="ct-btn ct-btn-ghost" onClick={() => setIdx(idx - 1)}>
                <I.chevLeft size={12} /> Back
              </button>
            )}
            {idx < steps.length - 1 ? (
              <button type="button" className="ct-btn ct-btn-primary" onClick={() => setIdx(idx + 1)}>
                Next <I.chevRight size={12} />
              </button>
            ) : (
              <button type="button" className="ct-btn ct-btn-primary" onClick={close}>
                <I.check size={12} /> Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardTour;
